import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {PHONE_TOUCH, phoneSheetSx} from 'src/components/mobile/mobileDesign.sx';
import {addActionButtonSx} from '../actionButton.sx';

export const headRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1.5,
    px: 2,
    py: 1.75,
    // No inset of its own on a phone. The strip that carries the list's name, its search, its
    // filters and its add button is as wide as the cards underneath it - one left edge and one
    // right edge down the whole screen. Any padding here made the strip narrower than the list it
    // belongs to, which is the misalignment that is impossible to name and impossible to unsee.
    [theme.breakpoints.down('md')]: {
        px: 0,
        py: '12px',
        gap: 1,
    },
};

export const titleGroupSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    minWidth: 0,
};

/**
 * The subject badge.
 *
 * Smaller than the page header's, because it now sits inside the card next to a 1rem title rather
 * than above one at 1.5rem. Same tinted square, so a reader coming from the old header recognises the
 * list by the same mark.
 */
export const iconBadgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    flexShrink: 0,
    '& svg': {
        fontSize: '18px',
    },
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
};

export const countSx: SxProps<Theme> = {
    color: theme.palette.neutral[500],
    fontVariantNumeric: 'tabular-nums',
};

export const headActionsSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    // Below md the search is as wide as the card and the filter trigger sits beside it. A flex item
    // will not go narrower than its content unless it is told it may.
    [theme.breakpoints.down('md')]: {
        width: '100%',
        // And the row wraps. It did not, so a search, an export button and the filter trigger were
        // all held on one line of a 390px screen and simply overlapped each other.
        flexWrap: 'wrap',
        '& > *': {
            minWidth: 0,
        },
        // The search takes the line it needs and the buttons follow it rather than fighting it.
        '& > .MuiOutlinedInput-root': {
            flex: '1 1 180px',
        },
    },
};

/**
 * Opens the filter sheet.
 *
 * Only below md, where the band itself is gone: four selects laid out one per line above every list
 * cost more of the screen than the list did, and they are read far less often than they are
 * scrolled past. Primary-edged once something is set, because a list showing eleven of four hundred
 * rows must not look like a list showing all four hundred.
 */
export const filterTriggerSx = (active?: boolean): SxProps<Theme> => ({
    '&.MuiIconButton-root': {
        flexShrink: 0,
        height: PHONE_TOUCH,
        minWidth: PHONE_TOUCH,
        px: 1.5,
        gap: 0.75,
        borderRadius: '12px',
        background: theme.palette.common.white,
        border: `1px solid ${active ? theme.palette.primary.main : theme.palette.neutral[200]}`,
        color: active ? theme.palette.primary.main : theme.palette.neutral[500],
        '&:hover': {
            background: theme.palette.common.white,
        },
        '& svg': {
            fontSize: '20px',
        },
    },
});

/** The same sheet the row menus rise in, so a phone has one way of asking for more. */
export const filterSheetSx: SxProps<Theme> = {
    ...(phoneSheetSx as object),
    '& .MuiDialog-container': {
        alignItems: 'flex-end',
    },
};

export const sheetHeadSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px 12px',
};

export const sheetTitleSx: SxProps<Theme> = {
    fontSize: '17px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: theme.palette.text.primary,
};

export const sheetClearSx: SxProps<Theme> = {
    minWidth: 0,
    padding: '12px 6px',
    margin: '-12px -6px',
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    color: theme.palette.neutral[600],
    '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
        color: theme.palette.neutral[800],
    },
    '&.Mui-disabled': {
        color: theme.palette.neutral[400],
        textDecoration: 'underline',
    },
};

/** Scrolls; the footer under it does not. */
export const sheetBodySx: SxProps<Theme> = {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '0 16px 8px',
    overflowY: 'auto',
};

/** One filter to a line, full width, at a finger's height. */
export const sheetFieldSx: SxProps<Theme> = {
    width: '100%',
    '& .MuiAutocomplete-root': {
        width: '100%',
    },
    '& .MuiOutlinedInput-root': {
        width: '100%',
        height: '44px',
        minHeight: '44px',
        backgroundColor: theme.palette.common.white,
    },
    '& .MuiAutocomplete-endAdornment': {
        top: 'calc(50% - 12px)',
    },
    '& .MuiAutocomplete-popupIndicator': {
        padding: 0,
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
        },
    },
};

export const sheetGroupSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
};

export const sheetGroupLabelSx: SxProps<Theme> = {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

/** Pinned: the one thing a sheet must never let scroll away. */
export const sheetFooterSx: SxProps<Theme> = {
    flexShrink: 0,
    display: 'flex',
    gap: '10px',
    padding: '14px 16px 22px',
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    backgroundColor: theme.palette.common.white,
};

export const sheetApplySx: SxProps<Theme> = {
    flexGrow: 1,
    height: 48,
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '15px',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0, 127, 220, 0.3)',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 127, 220, 0.3)',
    },
};

/**
 * The filter band.
 *
 * A surface of its own, bounded by the card, so it can wrap to a second line without moving the
 * heading above it. That is the whole point of the two tiers: the row that says what you are looking
 * at and the row that says what you do with it change size independently.
 *
 * Every line ends at the right edge, where Clear all is. A spacer did this only on bands without a
 * period; with one, the filters held the left end - invisible where a back office has enough filters
 * to fill the line, and a cluster stranded on the left for a driver with one or two.
 */
export const bandSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 1,
    px: 2,
    py: 1.25,
    backgroundColor: theme.palette.neutral[50],
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    borderBottom: `1px solid ${theme.palette.neutral[100]}`,
};

export const bandLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
    whiteSpace: 'nowrap',
};

/**
 * The gap between the two groups.
 *
 * Capped rather than free: an unbounded flex gap throws the period group at the far edge and leaves a
 * lake of grey in the middle of a band that holds three controls. This gives the groups air and stops.
 */
export const spacerSx: SxProps<Theme> = {
    flex: '1 1 auto',
    minWidth: theme.spacing(1),
    maxWidth: theme.spacing(6),
};

export const dividerSx: SxProps<Theme> = {
    width: '1px',
    height: theme.spacing(2.5),
    backgroundColor: theme.palette.neutral[200],
    display: {xs: 'none', md: 'block'},
};

/**
 * One place for the compact-field look.
 *
 * This was thirty lines of the same overrides copy-pasted onto each of the four autocompletes -
 * height, adornment offset, popup-indicator padding, icon size - so the band had four independent
 * opinions about how tall a control is, and a fifth filter meant a fifth copy.
 */
export const fieldSx = (wide?: boolean): SxProps<Theme> => ({
    width: theme.spacing((wide ? 200 : 156) / 8),
    // Below md a fixed width is a width the band does not have: four of these side by side push the
    // last one past the card edge. One filter per line instead, so the band grows downwards.
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
    '& .MuiAutocomplete-root': {
        width: '100%',
    },
    '& .MuiOutlinedInput-root': {
        height: '2rem',
        minHeight: '2rem',
        backgroundColor: theme.palette.common.white,
        // 2rem is a pointer target. Below md the same control is hit with a finger, which needs 44px.
        [theme.breakpoints.down('md')]: {
            height: '44px',
            minHeight: '44px',
        },
    },
    '& .MuiAutocomplete-endAdornment': {
        top: 'calc(50% - 12px)',
    },
    '& .MuiAutocomplete-popupIndicator': {
        padding: 0,
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
        },
    },
});

export const addButtonSx = addActionButtonSx('2rem');

export const clearAllSx: SxProps<Theme> = {
    minWidth: 0,
    p: 0,
    ml: 1,
    fontSize: '0.6875rem',
    fontWeight: 500,
    textTransform: 'none',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    color: theme.palette.neutral[600],
    '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.neutral[800],
    },
    '&.Mui-disabled': {
        color: theme.palette.neutral[400],
        textDecoration: 'underline',
    },
};

export const periodFieldSx: SxProps<Theme> = {
    width: theme.spacing(150 / 8),
    // Same as the filter fields: full width below md, exact pixel width from md up.
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
    '& .MuiOutlinedInput-root': {
        height: '2rem',
        minHeight: '2rem',
        backgroundColor: theme.palette.common.white,
        // 44px finger target below md.
        [theme.breakpoints.down('md')]: {
            height: '44px',
            minHeight: '44px',
        },
    },
};
